import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './E3Switch.module.css';

/**
 * E3Switch
 *  type ['text', 'email', 'password', 'number', 'color', 'search', 'tel'] 를 지원함
 * @type React.FC<E3SwitchPropTypes>
 */
const E3Switch = React.forwardRef((props, ref) => {
  Object.keys(props).forEach((p) => {
    if (!Object.keys(E3SwitchPropTypes).includes(p)) {
      console.error(`E3Switch: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  const refInput = useRef();
  const randomId = Math.random()
    .toString(36)
    .replace(/[^a-z]+/g, '')
    .substr(0, 5);

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 값 삭제(초기화) */
    clear() {
      setCurrState((prev) => ({
        ...prev,
        checked: props.defaultChecked ?? false,
      }));
    },
    /** 값 가져오기 */
    getValue() {
      const { checked } = currState;
      return checked;
    },
    /** 값 지정 */
    setValue(chk, e = null) {
      setCurrState((prev) => ({
        ...prev,
        checked: chk,
      }));
      e && _onChange();
    },
    setReadOnly: (isReadOnly) => {
      setCurrState((prev) => ({
        ...prev,
        readOnly: isReadOnly,
      }));
    },
    enable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: false,
      }));
    },
    disable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: true,
      }));
    },
    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error(
          'E3Switch: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다.'
        );
      }
    },
  }));

  /**
   * E3Switch Component의 State
   */
  const [currState, setCurrState] = useState({
    checked: props.defaultChecked,
    disabled: props.disabled,
    textLabel: props.textLabel,
    readOnly: props.readOnly,
  });

  /**
   * 변경 이벤트
   */
  const _onChange = (e) => {
    const newValue = e.target.checked;
    setCurrState((prev) => ({
      ...prev,
      checked: newValue,
    }));

    if (props.onChange) {
      props.onChange(e, newValue);
    }
  };

  const _handleKeyPress = (e) => {
    if (e.keyCode !== 32) return;

    e.preventDefault();
    _onChange();
  };

  return (
    <>
      <div
        className={`${styles.e3Switch} ${props.large ? '' : styles.smallSwitch} ${
          props.className ?? ''
        }`}
      >
        <input
          type="checkbox"
          ref={refInput}
          name={props.name}
          className={styles.checkbox}
          id={props.id + '_' + randomId}
          onChange={_onChange}
          checked={currState.checked}
          disabled={currState.disabled}
          readOnly={currState.readOnly}
        />
        <label
          className={styles.label}
          tabIndex={currState.disabled ? -1 : 1}
          onKeyDown={(e) => _handleKeyPress(e)}
          htmlFor={props.id + '_' + randomId}
        >
          <span
            className={
              currState.disabled ? `${styles.inner} ${styles.disabled}` : `${styles.inner}`
            }
            data-yes={props.optionLabels[0]}
            data-no={props.optionLabels[1]}
            tabIndex={-1}
          />
          <span
            className={
              currState.disabled ? `${styles.switch} ${styles.disabled}` : `${styles.switch}`
            }
            tabIndex={-1}
          />
        </label>
      </div>
      <label htmlFor={props.id + '_' + randomId}>{currState.textLabel}</label>
      {props.comment ? (
        <div class="con_comment">
          <label>{props.comment}</label>
        </div>
      ) : null}
    </>
  );
});

const E3SwitchPropTypes = {
  large: PropTypes.bool,
  name: PropTypes.string,
  id: PropTypes.string,
  defaultChecked: PropTypes.bool,
  disabled: PropTypes.bool,
  optionLabels: PropTypes.array,
  textLabel: PropTypes.string,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  comment: PropTypes.string,
};

E3Switch.propTypes = E3SwitchPropTypes;
E3Switch.defaultProps = {
  large: false,
  name: 'switch',
  id: 'switch',
  defaultChecked: false,
  disabled: false,
  optionLabels: ['ON', 'OFF'],
  textLabel: 'switch',
  readOnly: false,
  onChange: null,
  comment: null,
};
E3Switch.displayName = 'E3Switch';
export default E3Switch;
